/**
 * ChangePasswordCard — форма смены пароля.
 *
 * Презентационный компонент, который вызывает useParentSession().changePassword().
 * Используется на странице принудительной смены пароля, а в будущем —
 * в профиле родителя (раздел «Безопасность»).
 *
 * Валидация:
 * - все поля заполнены
 * - new ≥ 8 символов, содержит буквы и цифры
 * - new === confirm
 * - new !== current
 */
import React, { useState } from 'react';
import { Stack, Box, Text, PasswordInput, Button } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useParentSession } from '../context/ParentSessionContext';
import tokens from '../theme/designTokens';

const MIN_PASSWORD_LENGTH = 8;
const PASSWORD_HINT = 'Минимум 8 символов, должны быть буквы и цифры';

function validateNewPassword(value) {
  if (!value || value.length < MIN_PASSWORD_LENGTH) {
    return `Пароль должен быть не менее ${MIN_PASSWORD_LENGTH} символов`;
  }
  const hasLetter = /[a-zA-Zа-яА-Я]/.test(value);
  const hasDigit = /\d/.test(value);
  if (!hasLetter || !hasDigit) {
    return 'Пароль должен содержать буквы и цифры';
  }
  return null;
}

const ChangePasswordCard = ({ onSuccess, submitLabel = 'Сменить пароль' }) => {
  const { changePassword } = useParentSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      notifications.show({ title: 'Ошибка', message: 'Заполните все поля', color: 'red' });
      return;
    }

    const newPasswordError = validateNewPassword(newPassword);
    if (newPasswordError) {
      notifications.show({ title: 'Ошибка', message: newPasswordError, color: 'red' });
      return;
    }

    if (newPassword !== confirmPassword) {
      notifications.show({ title: 'Ошибка', message: 'Пароли не совпадают', color: 'red' });
      return;
    }

    if (newPassword === currentPassword) {
      notifications.show({
        title: 'Ошибка',
        message: 'Новый пароль должен отличаться от текущего',
        color: 'red',
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await changePassword(currentPassword, newPassword);
      if (!result.success) {
        notifications.show({
          title: 'Не удалось сменить пароль',
          message: result.error || 'Попробуйте ещё раз',
          color: 'red',
        });
        return;
      }
      notifications.show({
        title: 'Готово',
        message: 'Пароль успешно изменён',
        color: 'green',
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onSuccess?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <PasswordInput
          label="Текущий пароль"
          placeholder="••••••••"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          leftSection={<IconLock size={16} />}
          required
          size="md"
          radius="xl"
          autoComplete="current-password"
        />

        <Box>
          <PasswordInput
            label="Новый пароль"
            placeholder="••••••••"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            leftSection={<IconLock size={16} />}
            required
            size="md"
            radius="xl"
            autoComplete="new-password"
          />
          <Text size="xs" c={tokens.colors.text.muted} mt={4} ml={4}>
            {PASSWORD_HINT}
          </Text>
        </Box>

        <PasswordInput
          label="Повторите новый пароль"
          placeholder="••••••••"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          leftSection={<IconLock size={16} />}
          required
          size="md"
          radius="xl"
          autoComplete="new-password"
        />

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={submitting}
          color="brand-orange"
        >
          {submitLabel}
        </Button>
      </Stack>
    </form>
  );
};

export default ChangePasswordCard;
